import React, {Dispatch, FC, Fragment, MutableRefObject, ReactNode, RefObject, SetStateAction} from "react";
import cls from "./customInnerPage.module.scss";
import {IProduct, IProductOption, ISizeChart, TranslationsType} from "../../../types/homePageTypes";
import CustomProductOptions from "./CustomProductOptions";
import ModalLayout from "../../shared/ModalLayout";
import {useDispatch, useSelector} from "react-redux";
import {IState} from "../../../types/state";
import {openModalLayoutAction} from "../../../store/general";
import SizeChartContent from "./SizeChartContent";

interface IProps {
    product: IProduct;
    productName: string;
    productSkuOptionName: string;
    productSku: string;
    selectedCurrSymbol: string;
    children: ReactNode;
    priceHtmlAvailable: ReactNode;
    clientSidePrice: {
        price: string | number,
        specialPrice: string | number | null
    };
    productShortDescOptionName: string;
    productShortDesc: string;
    productOptions?: Array<IProductOption>;
    selectedCurrCode: string;
    translationsData: TranslationsType;
    refsMap?: MutableRefObject<Map<number, HTMLDivElement>>;
    sizeChart: ISizeChart | null;
    dbName: string;
    domain: string
}

const CustomInfoPart: FC<IProps> = (
    {
        product,
        productName,
        productSkuOptionName,
        productSku,
        selectedCurrSymbol,
        children,
        clientSidePrice,
        priceHtmlAvailable,
        productShortDescOptionName,
        productShortDesc,
        productOptions,
        selectedCurrCode,
        translationsData,
        refsMap,
        sizeChart,
        dbName,
        domain
    }
) => {
    const openModalLayout = useSelector((state: IState) => state.general.openModalLayout);
    const dispatch = useDispatch();
    const productOptionsJSX = <CustomProductOptions
        productOptions={productOptions}
        translationsData={translationsData}
        refsMap={refsMap}
    />
    const showProductOptionOrNot: Record<string, Record<string, ReactNode>> = {
        simple: {
            true: productOptionsJSX,
            false: <></>
        },
        virtual: {
            true: productOptionsJSX,
            false: <></>
        },
        configurable: {
            true: productOptionsJSX,
            false: <></>
        },
        downloadable: {
            true: <></>,
            false: <></>
        },
        bundle: {
            true: <></>,
            false: <></>
        },
    }

    const ShowPrice = () => {
        const pricePositionHandle = (whichPriceKey?: string) => {
            const finalPriceKey = whichPriceKey ?? "price"
            if (selectedCurrCode === "AMD") {
                return `${(clientSidePrice as Record<string, string>)[finalPriceKey]}` + " " + selectedCurrSymbol
            }
            return selectedCurrSymbol + " " + `${(clientSidePrice as Record<string, string>)[finalPriceKey]}`
        }
        const PriceValue = ({whichPriceKey}: {whichPriceKey?: string}) => (
            <div className={cls.price_value}>
                {pricePositionHandle(whichPriceKey)}
            </div>
        )
        return (
            <div className={cls.product__price}>
                {clientSidePrice.specialPrice ? (
                    <Fragment>
                        <div className={cls["product-card__new-price"]}>
                            <PriceValue whichPriceKey="specialPrice" />
                        </div>
                        <div className={cls["product-card__old-price"]}>
                            <PriceValue />
                        </div>
                    </Fragment>
                ) : <PriceValue />}
            </div>
        )
    }
    const ShowSizeChart = () => {
        if (sizeChart) {
            return <Fragment>
                <p
                    className={cls["product-inner-chart-title"]}
                    style={{ margin: "12px 0", cursor: "pointer" }}
                    onClick={() => dispatch(openModalLayoutAction(true))}
                >
                    {translationsData?.ViewSizeChart}
                </p>
                {openModalLayout
                    ? <ModalLayout close={() => dispatch(openModalLayoutAction(false))} title={sizeChart?.template_name}>
                        <SizeChartContent sizeChart={sizeChart} dbName={dbName} domain={domain}/>
                    </ModalLayout>
                    : null
                }
            </Fragment>
        }
        return null
    }

    const showShortDesc = {
        "true": <div className={cls["product-inner-description-title"]}>{productShortDescOptionName}</div>,
        "false": <></>
    }

    return (
        <Fragment>
            <h1 style={{textAlign: "left"}} className={cls.product__name}>{productName}</h1>
            <p className={cls.product__sku}>{productSkuOptionName}: {productSku}</p>
            <ShowPrice />
            <ShowSizeChart />
            <div>{priceHtmlAvailable}</div>
            {showShortDesc[`${productShortDesc?.length > 0}`]}
            <div dangerouslySetInnerHTML={{__html: productShortDesc}}/>
            {showProductOptionOrNot[`${product.type}`][`${!!(productOptions && productOptions?.length)}`]}
            {children}
        </Fragment>
    )
};

export default CustomInfoPart;
